import { FormControlLabel, Grid, MenuItem, Radio, RadioGroup, Select, TextField } from "@mui/material";
import * as React from "react";
import { CheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/CheckboxDropdown";
import { Base } from "ui/Scripts/source/framework/base";
import { FileAttachment, IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";
import { ConfirmationDialogResult, EnumHelper, RecordEmployeeType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { SaveAttachmentBase64 } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/models/common/saveAttachmentBase64";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import { IRecordEditNew } from "../../../models/record/recordEditNew";
import * as recordService from "../../../models/services/recordService";
import * as userService from "../../../models/services/userService";
import { IUserListItem } from "../../../models/user/userListItem";
import { Dialog } from "../../framework/Dialog/Dialog";
import { TextFieldWithButtonGroup } from "../../framework/Form/TextFieldWithButtonGroup";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../framework/Grid/GridRow";
import { SelectUnitDialog } from "../../framework/Unit/SelectUnitDialog";
import { SelectUserDialog } from "../../user/SelectUserDialog/SelectUserDialog";
import { FormPassword } from "source/components/framework/FormPassword";

/* eslint-disable no-unused-vars */
const enum NewRecordType {
    NewRecord = "1",
    NewRecordAndNewUser = "2",
    NewRecordAndExistingUser = "3"
}

const enum NewRecordDialogPage {
    Selection = "1",
    NewRecord = "2",
    NewRecordAndNewUser = "3",
    NewRecordAndExistingUser = "4"
}
/* eslint-enable no-unused-vars */

export interface IRecordEditNewRecordDialogProp {
    classes?: string;
    companyId: string;
    unitId: string;
    unitCode: string;
    employeeType: RecordEmployeeType;
    onOk: (recordId: string) => void;
    onCancel: () => void;
}

export interface IRecordEditNewRecordDialogState {
    page: NewRecordDialogPage;
    recordEditNew: IRecordEditNew;
    // Selection
    recordType: NewRecordType;
    // NewRecord
    firstName: string;
    lastName: string;
    email: string;
    employeeType: RecordEmployeeType;
    unitId: string;
    unitCode: string;
    showSelectUnitDialog: boolean;
    // NewRecordAndNewUser
    userCode: string;
    userPassword: string;
    userPasswordConfirmation: string;
    userGroupIds: string[];
    // NewRecordAndExistingUser
    selectedUserId: string;
    selectedUserCode: string;
    selectedUserPictureFile: IFileAttachment;
    showSelectUserDialog: boolean;
}

export class RecordEditNewRecordDialog extends React.Component<IRecordEditNewRecordDialogProp, IRecordEditNewRecordDialogState> {
    private orgStateHash: string = "";
    
    getStateFromProps = (props: IRecordEditNewRecordDialogProp): IRecordEditNewRecordDialogState => {
        return {
            page: NewRecordDialogPage.Selection,
            recordEditNew: null,
            // Selection
            recordType: NewRecordType.NewRecord,
            // NewRecord
            firstName: "",
            lastName: "",
            email: "",
            employeeType: props.employeeType,
            unitId: props.unitId,
            unitCode: props.unitCode,
            showSelectUnitDialog: false,
            // NewRecordAndNewUser
            userCode: "",
            userPassword: "",
            userPasswordConfirmation: "",
            userGroupIds: [],
            // NewRecordAndExistingUser
            selectedUserId: "",
            selectedUserCode: "",
            selectedUserPictureFile: null,
            showSelectUserDialog: false
        };
    };

    constructor(props: IRecordEditNewRecordDialogProp) {
        super(props);
        this.state = this.getStateFromProps(props);
        this.orgStateHash = this.getHashFromState(this.state);
    }

    async componentDidMount(): Promise<void> {
        const props = this.props;
        const recordEditNew = await AppUtils.callService(() => recordService.getRecordEditNew(props.companyId), false, false);
        if (!recordEditNew) return;
        const userPassword = Base.generatePassword(Math.max(recordEditNew.minPasswordLength, 16));
        this.setState({
            recordEditNew,
            userPassword,
            userPasswordConfirmation: userPassword
        });
    }
    
    handleChangePage = (page: NewRecordDialogPage) => {
        this.setState({
            page
        });
    };

    handleChangePageByRecordType = (recordType: NewRecordType) => {
        this.setState({
            page: recordType === NewRecordType.NewRecord
                ? NewRecordDialogPage.NewRecord
                : (recordType === NewRecordType.NewRecordAndNewUser
                    ? NewRecordDialogPage.NewRecordAndNewUser
                    : NewRecordDialogPage.NewRecordAndExistingUser)
        });
    };

    handleChangeRecordType = (checked: boolean, recordType: NewRecordType) => {
        if (!checked) return;
        this.setState({
            recordType
        });
    };

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        // NewRecord
        if (name === "firstName") {
            this.setState({ firstName: value });
        } else if (name === "lastName") {
            this.setState({ lastName: value });
        } else if (name === "email") {
            this.setState({ email: value });
        } else if (name === "employeeType") {
            this.setState({ employeeType: value.toInteger() });
        // NewRecordAndNewUser
        } else if (name === "xts") {
            this.setState({ userCode: value });
        }
    };

    handleBlur = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "xts") {
            AppUtils.callService(() => userService.checkUserCode(Base.emptyGuid, this.props.companyId, value), false, true);
        }
    };

    handleUserGroupsChange = (selectedIds: string[]) => {
        this.setState({ userGroupIds: selectedIds });
    };

    getHashFromState = (state: IRecordEditNewRecordDialogState): string => {
        const result = new SaveDataObj();
        result.append("firstName", state.firstName);
        result.append("lastName", state.lastName);
        result.append("email", state.email);
        result.append("employeeType", state.employeeType);
        result.append("unitId", state.unitId);
        result.append("userCode", state.userCode);
        result.append("userPassword", state.userPassword);
        result.append("userGroupIds", state.userGroupIds);
        result.append("selectedUserId", state.selectedUserId);
        result.append("selectedUserCode", state.selectedUserCode);
        return result.hash;
    };
    
    // #region Select Unit
    handleSelectUnit = () => {
        this.setState({
            showSelectUnitDialog: true
        });
    };

    handleSelectUnitDialogOk = async (node: IIdTitleNode) => {
        if (!node) return;
        this.setState({
            showSelectUnitDialog: false,
            unitId: node.id,
            unitCode: node.title
        });
    };

    handleSelectUnitDialogCancel = () => {
        this.setState({
            showSelectUnitDialog: false
        });
    };
    // #endregion Select Unit

    // #region Select User
    handleSelectUser = () => {
        this.setState({
            showSelectUserDialog: true
        });
    };

    onSelectUserDialogOk = async (users: IUserListItem[]) => {
        if (!users || users.length < 1) return;
        const user = users[0];
        if (!user) return;
        const pictureFile = user.defaultPictureInUse ? null : await FileAttachment.photoToFileAttachment(user.picture, 0);
        this.setState({
            showSelectUserDialog: false,
            selectedUserId: user.id,
            selectedUserCode: user.code,
            selectedUserPictureFile: pictureFile,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email
        });
    };

    onSelectUserDialogCancel = () => {
        this.setState({
            showSelectUserDialog: false
        });
    };
    // #endregion Select User

    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            // NewRecord
            if (!state.firstName) {
                AppUtils.showErrorMessage(Translations.FirstNameMustBeDefined);
                return resolve(false);
            }
            if (!state.lastName) {
                AppUtils.showErrorMessage(Translations.LastNameMustBeDefined);
                return resolve(false);
            }
            if (state.employeeType > 98) {
                AppUtils.showErrorMessage(Translations.EmployeeTypeMustBeDefined);
                return resolve(false);
            }
            if (!Base.isValidEmailAddress(state.email)) {
                AppUtils.showErrorMessage(Translations.EmailAddressMustBeValid);
                return resolve(false);
            }
            if (!state.unitId) {
                AppUtils.showErrorMessage(Translations.UnitMustBeSelected);
                return resolve(false);
            }
            // NewRecordAndNewUser
            if (state.page === NewRecordDialogPage.NewRecordAndNewUser) {
                if (!Base.isValidUserCode(state.userCode)) {
                    AppUtils.showErrorMessage(Translations.UserCodeMustBeValid);
                    return resolve(false);
                }
                const errorMessage = Base.isValidPassword(state.userPassword, state.recordEditNew.minPasswordLength);
                if (errorMessage) {
                    AppUtils.showErrorMessage(errorMessage);
                    return resolve(false);
                }
                if (state.userPassword !== state.userPasswordConfirmation) {
                    AppUtils.showErrorMessage(Translations.PasswordConfirmationDoesNotMatchPassword);
                    return resolve(false);
                }
                if (state.userGroupIds.length < 1) {
                    AppUtils.showErrorMessage(Translations.UserGroupsMustBeDefined);
                    return resolve(false);
                }
            }
            // NewRecordAndExistingUser
            if (state.page === NewRecordDialogPage.NewRecordAndExistingUser) {
                if (!state.selectedUserId) {
                    AppUtils.showErrorMessage(Translations.UserAccountMustBeSelected);
                    return resolve(false);
                }
            }
            return resolve(true);
        });
    };
    
    getSaveData = async (props: IRecordEditNewRecordDialogProp, state: IRecordEditNewRecordDialogState): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("unitId", state.unitId ? state.unitId : null);
        result.append("firstName", state.firstName);
        result.append("lastName", state.lastName);
        result.append("email", state.email);
        result.append("employeeType", state.employeeType);
        if (state.recordEditNew.canEditUsers && state.page === NewRecordDialogPage.NewRecordAndNewUser) {
            result.append("newUserCode", state.userCode);
            result.append("newUserPassword", state.userPassword);
            result.append("newUserPasswordConfirmation", state.userPasswordConfirmation);
            result.append("newUserGroupIds", state.userGroupIds);
        }
        if (state.recordEditNew.canReadUsers && state.page === NewRecordDialogPage.NewRecordAndExistingUser) {
            result.append("selectedUserCode", state.selectedUserCode);
            if (state.selectedUserPictureFile) {
                result.append("pictureAttachments", await SaveAttachmentBase64.fromFileAttachments([state.selectedUserPictureFile]));
            }
        }
        return result;
    };

    saveToDb = async (): Promise<SaveSuccess> => {
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        // Set data
        const saveData = await this.getSaveData(this.props, state);
        // Call server
        const result = await AppUtils.callService(() => recordService.saveRecordEditNew(saveData.obj));
        if (!result) return null;
        this.orgStateHash = this.getHashFromState(this.state);
        return result;
    };

    handleSave = async (): Promise<SaveSuccess> => {
        const result = await this.saveToDb();
        if (result) {
            this.props.onOk(result.id);
        }
        return result;
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getHashFromState(state), this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave();
        } else {
            props.onCancel();
        }
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.recordEditNew) return null;
        return (
            <Dialog
                id="record-edit-new-record-dialog"
                maxWidth={state.page === NewRecordDialogPage.Selection ? "sm" : "md"}
                title={state.page === NewRecordDialogPage.Selection
                    ? Translations.AddRecordUser
                    : (state.page === NewRecordDialogPage.NewRecord
                        ? Translations.AddRecordUserWithoutUserAccount
                        : (state.page === NewRecordDialogPage.NewRecordAndNewUser
                            ? Translations.AddRecordUserAndUserAccount
                            : Translations.AddRecordUserToUserAccount))}
                body={
                    <div className="dialogContent small-control-labels">
                        {state.page === NewRecordDialogPage.Selection &&
                            <>
                                <GridRow>
                                    <Grid item xs>
                                        <FormField title={Translations.SelectRecordUserTypeToBeCreated} margin={0}>
                                            <RadioGroup value={state.recordType} onChange={(_e, value) => this.handleChangeRecordType(true, value as NewRecordType)}>
                                                <FormControlLabel
                                                    label={Translations.AddRecordUserWithoutUserAccount}
                                                    value={NewRecordType.NewRecord}
                                                    control={
                                                        <Radio />
                                                    }
                                                />
                                                {state.recordEditNew.canEditUsers &&
                                                    <FormControlLabel
                                                        label={Translations.AddRecordUserAndUserAccount}
                                                        value={NewRecordType.NewRecordAndNewUser}
                                                        control={
                                                            <Radio />
                                                        }
                                                    />
                                                }
                                                {state.recordEditNew.canReadUsers &&
                                                    <FormControlLabel
                                                        label={Translations.AddRecordUserToUserAccount}
                                                        value={NewRecordType.NewRecordAndExistingUser}
                                                        control={
                                                            <Radio />
                                                        }
                                                    />
                                                }
                                            </RadioGroup>
                                        </FormField>
                                    </Grid>
                                </GridRow>
                            </>
                        }
                        {state.page === NewRecordDialogPage.NewRecordAndExistingUser &&
                            <GridRow>
                                <Grid item sm={6}>
                                    <FormField title={Translations.UserName} margin={"0 0 1.5rem 0"}>
                                        <TextFieldWithButtonGroup
                                            type="text"
                                            name="userCode"
                                            title={Translations.UserName}
                                            disabled={true}
                                            value={state.selectedUserCode}
                                            buttons={[
                                                { label: Translations.Select, onClick: this.handleSelectUser }
                                            ]}
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                        }
                        {(state.page === NewRecordDialogPage.NewRecord || state.page === NewRecordDialogPage.NewRecordAndNewUser || state.page === NewRecordDialogPage.NewRecordAndExistingUser) &&
                            <>
                                <GridRow>
                                    <Grid item xs>
                                        <FormField title={Translations.FirstName} required margin={0}>
                                            <TextField type="text" name="firstName" title={Translations.FirstName} inputProps={{ maxLength: 50 }} value={state.firstName} onChange={this.handleChange} />
                                        </FormField>
                                    </Grid>
                                    <Grid item xs>
                                        <FormField title={Translations.LastName} required margin={0}>
                                            <TextField type="text" name="lastName" title={Translations.LastName} inputProps={{ maxLength: 50 }} value={state.lastName} onChange={this.handleChange} />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                                <GridRow>
                                    <Grid item xs>
                                        <FormField title={Translations.EmployeeType} required>
                                            <Select name="employeeType" title={Translations.EmployeeType} value={state.employeeType.toString(10)} onChange={this.handleChange}>
                                                <MenuItem key={99} value="99">{"'" + Translations.Select + "'"}</MenuItem>
                                                <MenuItem key={RecordEmployeeType.Intern} value={RecordEmployeeType.Intern.toString(10)}>{EnumHelper.RecordEmployeeTypeToStr(RecordEmployeeType.Intern)}</MenuItem>
                                                <MenuItem key={RecordEmployeeType.Employee} value={RecordEmployeeType.Employee.toString(10)}>{EnumHelper.RecordEmployeeTypeToStr(RecordEmployeeType.Employee)}</MenuItem>
                                                <MenuItem key={RecordEmployeeType.AgencyWorker} value={RecordEmployeeType.AgencyWorker.toString(10)}>{EnumHelper.RecordEmployeeTypeToStr(RecordEmployeeType.AgencyWorker)}</MenuItem>
                                                <MenuItem key={RecordEmployeeType.SeasonalWorker} value={RecordEmployeeType.SeasonalWorker.toString(10)}>{EnumHelper.RecordEmployeeTypeToStr(RecordEmployeeType.SeasonalWorker)}</MenuItem>
                                                <MenuItem key={RecordEmployeeType.Entrepreneur} value={RecordEmployeeType.Entrepreneur.toString(10)}>{EnumHelper.RecordEmployeeTypeToStr(RecordEmployeeType.Entrepreneur)}</MenuItem>
                                                <MenuItem key={RecordEmployeeType.Other} value={RecordEmployeeType.Other.toString(10)}>{EnumHelper.RecordEmployeeTypeToStr(RecordEmployeeType.Other)}</MenuItem>
                                            </Select>
                                        </FormField>
                                    </Grid>
                                    <Grid item xs>
                                        <FormField title={Translations.EmailAddress}>
                                            <TextField type="text" name="email" title={Translations.EmailAddress} inputProps={{ maxLength: 50 }} disabled={false} value={state.email} onChange={this.handleChange} />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                                <GridRow>
                                    <Grid item sm={6}>
                                        <FormField title={Translations.Unit} required>
                                            <TextFieldWithButtonGroup
                                                type="text"
                                                name="unitCode"
                                                title={Translations.Unit}
                                                disabled={true}
                                                value={state.unitCode}
                                                buttons={[
                                                    { label: Translations.Select, onClick: this.handleSelectUnit }
                                                ]}
                                            />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                            </>
                        }
                        {state.page === NewRecordDialogPage.NewRecordAndNewUser &&
                            <>
                                <GridRow>
                                    <Grid item sm={6}>
                                        <FormField title={Translations.UserName} required>
                                            <TextField type="text" name="xts" title={Translations.UserName} inputProps={{ maxLength: 50 }} value={state.userCode} onChange={this.handleChange} onBlur={this.handleBlur} />
                                        </FormField>
                                    </Grid>
                                    <Grid item sm={6}>
                                        <FormField title={Translations.UserGroups} required>
                                            <CheckboxDropdown
                                                selectedIds={state.userGroupIds}
                                                values={state.recordEditNew.userGroups}
                                                onChange={this.handleUserGroupsChange}
                                            />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                                <GridRow>
                                    <Grid item xs>
                                        <FormField title={Translations.Password} required>
                                            <FormPassword
                                                tooltip={Translations.Password}
                                                maxLength={50}
                                                value={state.userPassword}
                                                required={true}
                                                onChange={(userPassword) => this.setState({ userPassword })}
                                            />
                                        </FormField>
                                    </Grid>
                                    <Grid item xs>
                                        <FormField title={Translations.ConfirmPassword} required>
                                            <FormPassword
                                                tooltip={Translations.ConfirmPassword}
                                                maxLength={50}
                                                value={state.userPasswordConfirmation}
                                                required={true}
                                                onChange={(userPasswordConfirmation) => this.setState({ userPasswordConfirmation })}
                                            />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                            </>
                        }
                        {state.showSelectUserDialog &&
                            <SelectUserDialog
                                companyId={props.companyId}
                                ignoreQrCodeUsers={true}
                                ignoreRecordUserConnected={true}
                                onOk={this.onSelectUserDialogOk}
                                onCancel={this.onSelectUserDialogCancel}
                            />
                        }
                        {state.showSelectUnitDialog &&
                            <SelectUnitDialog
                                nodes={state.recordEditNew.units.filter(i => i.id !== Base.emptyGuid)}
                                title={Translations.SelectUnit}
                                firstColumnTitle={Translations.Unit}
                                selectedId={state.unitId}
                                onOk={this.handleSelectUnitDialogOk}
                                onCancel={this.handleSelectUnitDialogCancel}
                            />
                        }
                    </div>}
                buttons={[
                    { title: Translations.Cancel, variant: "outlined", onClick: this.handleCancel },
                    { title: Translations.Previous, variant: "outlined", onClick: state.page === NewRecordDialogPage.Selection ? null : () => this.handleChangePage(NewRecordDialogPage.Selection) },
                    { title: state.page === NewRecordDialogPage.Selection ? Translations.Next : Translations.Save, variant: "confirmation", onClick: state.page === NewRecordDialogPage.Selection ? () => this.handleChangePageByRecordType(state.recordType) : this.handleSave },
                ]}
                onClose={this.handleCancel}/>
        );
    }
}